// npm i qrcode
import { h, Component } from 'preact';
import * as QRCode from 'qrcode';

function rgbToHex(rgb) {
    // 'rgb(33, 37, 41)' or 'rgba(33, 37, 41, 1)'
    const m = String(rgb).match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!m) return null;
    const [r, g, b] = m.slice(1, 4).map(n => Math.max(0, Math.min(255, parseInt(n, 10))));
    const to2 = n => n.toString(16).padStart(2, '0');
    return `#${to2(r)}${to2(g)}${to2(b)}`;
}

function resolveCssColorToHex(input, scope = document.documentElement) {
    // Already hex?
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(input)) return input;

    // Map 'transparent' to current body background (feels transparent across modes)
    if (String(input).toLowerCase() === 'transparent') {
        const bg = getComputedStyle(scope).getPropertyValue('--bs-body-bg').trim() || '#ffffff';
        return resolveCssColorToHex(bg, scope);
    }

    // CSS var or named color / rgb(): compute it
    const probe = document.createElement('span');
    probe.style.display = 'none';
    // Prefer color property so computed style will give a color string
    probe.style.color = input.startsWith('var(') ? `var(${input.slice(4, -1)})` : input;
    scope.appendChild(probe);
    const computed = getComputedStyle(probe).color;
    probe.remove();

    // If computed is empty, last resort try reading as bg
    const hex = rgbToHex(computed) || rgbToHex(getComputedStyle(scope).backgroundColor);
    return hex || '#000000';
}

class QrCode extends Component {
    constructor(props) {
        super(props);
        this.setRef = el => { this.host = el; };
        this.renderToken = 0;
    }

    static defaultProps = {
        value: '',
        size: 160,
        level: 'M',            // 'L' | 'M' | 'Q' | 'H'
        margin: 0,
        colorDark: 'var(--bs-emphasis-color)',
        colorLight: 'transparent',
        renderer: 'svg',       // 'svg' | 'canvas'
        className: '',
        style: ''
    };

    componentDidMount() { this.renderQr(); }
    componentDidUpdate() { this.renderQr(); }
    componentWillUnmount() {
        this.renderToken++;
        if (this.host) this.host.innerHTML = '';
    }

    renderQr() {
        const {
            value, size, level, margin, colorDark, colorLight, renderer, className, style,
            preserveAspectRatio
        } = this.props;
        if (!this.host || !value) return;

        // The style prop is a string on the svg; an object would serialize as
        // "[object Object]", so only strings are forwarded to the rendered svg.
        const svgStyle = typeof style === 'string' ? style : '';

        // Resolve colors to hex up front (qrcode needs hex)
        const scope = document.documentElement;
        const darkHex = resolveCssColorToHex(colorDark, scope);
        const lightHex = resolveCssColorToHex(colorLight, scope);


        const opts = {
            errorCorrectionLevel: level,
            margin,
            color: { dark: darkHex, light: lightHex }
        };

        const token = ++this.renderToken;

        const renderSvg = () => {
            QRCode.toString(String(value), { ...opts, type: 'svg' }, (err, svg) => {
                if (token !== this.renderToken) return;
                if (err || !svg) {
                    console.error('QrCode svg render failed:', err);
                    renderCanvas();
                    return;
                }
                this.host.innerHTML = svg;
                const svgEl = this.host.firstElementChild;
                if (svgEl) {
                    svgEl.setAttribute('class', className);
                    svgEl.setAttribute('width', String(size));
                    svgEl.setAttribute('height', String(size));
                    // The caller's style has to come last: it is what lets a
                    // label scale the code with width/height instead of the
                    // fixed `size` px above. (This used to be set and then
                    // immediately overwritten, so it never had any effect.)
                    svgEl.setAttribute('style', `display:block; vertical-align:top;${svgStyle ? ` ${svgStyle}` : ''}`);
                    if (preserveAspectRatio) {
                        svgEl.setAttribute('preserveAspectRatio', preserveAspectRatio);
                    }
                    svgEl.setAttribute('aria-hidden', 'true');
                    svgEl.setAttribute('focusable', 'false');
                }
            });
        };

        const renderCanvas = () => {
            let canvas = this.host.querySelector('canvas');
            if (!canvas) {
                this.host.innerHTML = '';
                canvas = document.createElement('canvas');
                this.host.appendChild(canvas);
            }
            canvas.width = size;
            canvas.height = size;
            QRCode.toCanvas(canvas, String(value), opts, err => {
                if (token !== this.renderToken) return;
                if (err) console.error('QrCode canvas render failed:', err);
                canvas.style.display = 'block';
                canvas.style.verticalAlign = 'top';
            });
        };

        if (renderer === 'canvas') renderCanvas();
        else renderSvg();
    }

    render() {
        const { value, className, style } = this.props;
        if (!value) return null;
        return (
            <span
                ref={this.setRef}
                class={className}
                style={style}
                role='img'
                aria-label='qrcode'
            />
        );
    }
}

export default QrCode;
